import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Drawer } from '../../components/Drawer.tsx';
import { Button, Field, TextField, SelectField } from '../../components/ui.tsx';
import { useScreens } from '../../api/screens.ts';
import { useGroups } from '../../api/groups.ts';
import { ApiError } from '../../api/client.ts';
import { timezoneOptions } from '../../lib/tz.ts';
import {
  useCreateReportSchedule,
  useUpdateReportSchedule,
  REPORT_METRICS,
  type ReportSchedule,
  type ReportScopeType,
  type ReportFreq,
  type ReportMetric,
  type CreateReportSchedule,
} from '../../api/reports.ts';

export const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const METRIC_LABEL: Record<ReportMetric, string> = {
  uptime: 'Uptime',
  playback: 'Playback',
  top_media: 'Top content',
  attention: 'Attention',
};

/**
 * Create/edit drawer for a scheduled Slack report. `initial` undefined = create.
 * Weekly reports require a weekday; at least one metric is required — Save stays
 * disabled until both hold (the API validates the same rules).
 */
export function ReportScheduleDrawer({
  initial,
  defaultTz,
  onClose,
}: {
  initial?: ReportSchedule;
  defaultTz: string;
  onClose: () => void;
}) {
  const editing = !!initial;
  const create = useCreateReportSchedule();
  const update = useUpdateReportSchedule();
  const screens = useScreens();
  const groups = useGroups();
  const tzOptions = useMemo(() => timezoneOptions(), []);

  const [name, setName] = useState(initial?.name ?? '');
  const [scopeType, setScopeType] = useState<ReportScopeType>(initial?.scope_type ?? 'org');
  const [scopeId, setScopeId] = useState<string>(initial?.scope_id ?? '');
  const [freq, setFreq] = useState<ReportFreq>(initial?.freq ?? 'weekly');
  // '' = unpicked, so a weekly report can't be saved without an explicit weekday.
  const [dayOfWeek, setDayOfWeek] = useState<number | ''>(initial?.day_of_week ?? '');
  const [hour, setHour] = useState<number>(initial?.hour ?? 9);
  const [timezone, setTimezone] = useState<string>(initial?.timezone ?? defaultTz);
  const [metrics, setMetrics] = useState<Set<ReportMetric>>(
    new Set(initial?.metrics ?? (['uptime', 'playback'] as ReportMetric[])),
  );
  const [slackMode, setSlackMode] = useState<'dm' | 'channel'>(initial?.slack_target ? 'channel' : 'dm');
  const [channel, setChannel] = useState(initial?.slack_target ?? '');

  const toggleMetric = (m: ReportMetric) =>
    setMetrics((prev) => {
      const next = new Set(prev);
      if (next.has(m)) next.delete(m);
      else next.add(m);
      return next;
    });

  const needsTarget = scopeType !== 'org';
  const scopeReady = !needsTarget || !!scopeId;
  const weekdayReady = freq !== 'weekly' || dayOfWeek !== '';
  const canSave =
    name.trim().length > 0 && scopeReady && weekdayReady && metrics.size > 0 && !create.isPending && !update.isPending;

  function submit() {
    const body: CreateReportSchedule = {
      name: name.trim(),
      scope_type: scopeType,
      ...(needsTarget ? { scope_id: scopeId } : {}),
      freq,
      ...(freq === 'weekly' ? { day_of_week: Number(dayOfWeek) } : {}),
      hour,
      timezone,
      metrics: [...metrics],
      slack_target: slackMode === 'channel' ? channel.trim() || null : null,
    };
    const onErr = (e: unknown) => toast.error(e instanceof ApiError ? e.message : 'Failed to save');
    if (editing) {
      update.mutate(
        { id: initial!.id, body },
        { onSuccess: () => { toast.success('Report updated'); onClose(); }, onError: onErr },
      );
    } else {
      create.mutate(body, {
        onSuccess: () => { toast.success('Report scheduled'); onClose(); },
        onError: onErr,
      });
    }
  }

  return (
    <Drawer title={editing ? 'Edit report' : 'New scheduled report'} onClose={onClose}>
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Weekly fleet digest"
      />

      <SelectField
        label="Scope"
        value={scopeType}
        onChange={(e) => {
          setScopeType(e.target.value as ReportScopeType);
          setScopeId('');
        }}
      >
        <option value="org">Whole organization</option>
        <option value="group">A group</option>
        <option value="screen">A single screen</option>
      </SelectField>

      {scopeType === 'group' && (
        <SelectField label="Group" value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
          <option value="">Pick a group…</option>
          {(groups.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>{g.name}</option>
          ))}
        </SelectField>
      )}
      {scopeType === 'screen' && (
        <SelectField label="Screen" value={scopeId} onChange={(e) => setScopeId(e.target.value)}>
          <option value="">Pick a screen…</option>
          {(screens.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </SelectField>
      )}

      <div className="field-row">
        <SelectField label="Frequency" value={freq} onChange={(e) => setFreq(e.target.value as ReportFreq)}>
          <option value="daily">Daily</option>
          <option value="weekly">Weekly</option>
        </SelectField>
        {freq === 'weekly' && (
          <SelectField
            label="Weekday"
            value={dayOfWeek === '' ? '' : String(dayOfWeek)}
            onChange={(e) => setDayOfWeek(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Pick a day…</option>
            {WEEKDAYS.map((d, i) => (
              <option key={d} value={i}>{d}</option>
            ))}
          </SelectField>
        )}
      </div>

      <div className="field-row">
        <SelectField label="Hour" value={String(hour)} onChange={(e) => setHour(Number(e.target.value))}>
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
          ))}
        </SelectField>
        <SelectField label="Timezone" value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          {tzOptions.map((tz) => (
            <option key={tz} value={tz}>{tz}</option>
          ))}
        </SelectField>
      </div>

      <Field label="Include">
        <div className="a8-metric-checks">
          {REPORT_METRICS.map((m) => (
            <label key={m} className={`a8-check${metrics.has(m) ? ' on' : ''}`}>
              <input type="checkbox" checked={metrics.has(m)} onChange={() => toggleMetric(m)} />
              {METRIC_LABEL[m]}
            </label>
          ))}
        </div>
      </Field>

      <Field label="Deliver to">
        <div className="a8-radio-group" role="radiogroup" aria-label="Slack destination">
          <label className="a8-radio">
            <input
              type="radio"
              name="slack-dest"
              checked={slackMode === 'dm'}
              onChange={() => setSlackMode('dm')}
            />
            DM me
          </label>
          <label className="a8-radio">
            <input
              type="radio"
              name="slack-dest"
              checked={slackMode === 'channel'}
              onChange={() => setSlackMode('channel')}
            />
            Channel ID
          </label>
        </div>
      </Field>
      {slackMode === 'channel' && (
        <TextField
          label="Slack channel ID"
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          placeholder="C0123456789"
          hint="Find it in Slack: channel name → About → Channel ID."
        />
      )}

      <p className="hint mt-2">Delivered via Slack. Email delivery is coming later.</p>

      <div className="row mt-4">
        <Button variant="primary" onClick={submit} disabled={!canSave}>
          {editing ? 'Save changes' : 'Schedule report'}
        </Button>
        <Button onClick={onClose}>Cancel</Button>
      </div>
    </Drawer>
  );
}
